import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import Autocomplete from "react-google-autocomplete";
import { useLocation } from "react-router-dom";
import {
  Button,
  Grid,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  Modal,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  companyIndustryMap,
  companySubSectorMap,
  companySizeMap,
  pictureRatios,
} from "bundles/common/utils/constants";
import RichTextEditor from "bundles/common/components/RichTextEditor";
import PictureDropzone from "bundles/common/components/PictureDropzone";
import { uploadPicture } from "bundles/common/utils/uploadProfilePicture";
import ImageCropper from "bundles/common/components/ImageCropper";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import Bugsnag from "@bugsnag/js";
import EditProjectModal from "./EditProjectModal";
import { PlusCircle, Trash2, X, ImagePlus } from "lucide-react";
import { sortDateRange, normalizeRegion } from "bundles/common/utils/utils";
import { useDropzone } from "react-dropzone";
import ProjectSettingsView from "./ProjectSettingsView";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants";

// Rounded outlined inputs with a green focus/hover border (matches the app design system).
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& label.Mui-focused": {
    color: "hsl(153, 68%, 25%)",
  },
};

// Shared card wrapper for grouped settings sections.
const cardSx = {
  border: "1px solid hsl(220, 13%, 91%)",
  borderRadius: "12px",
  p: { xs: "20px", md: "24px" },
  mb: "24px",
};

const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const Company = () => {
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const currentCompany = currentRecruiter.company;
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const dispatch = useDispatch();

  const query = useQuery();
  const theme = useTheme();
  const intl = useIntl();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { enqueueSnackbar } = useSnackbar();

  const [isCropModalOpened, setIsCropModalOpened] = useState(false);
  const [logo, setLogo] = useState(currentCompany.logo_url);

  //This extra is needed for the cropper
  const [logoUrl, setLogoUrl] = useState(currentCompany.logo_url);
  const [coverPicture, setCoverPicture] = useState(
    currentCompany.cover_picture_url
  );
  //This extra is needed for the cropper
  const [coverPictureUrl, setCoverPictureUrl] = useState(
    currentCompany.cover_picture_url
  );
  const [cropable, setCropable] = useState("cover_picture");
  const [isCropped, setIsCropped] = useState(false);

  const [generalInfoErrors, setGeneralInfoErrors] = useState({});
  const [detailedInfoErrors] = useState({});

  const [isEditModalOpen, setIsEditModalOpen] = useState(query.get("newProject") === "true" || false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isNew, setIsNew] = useState(null);
  const [firstPartAndNew, setFirstPartAndNew] = useState(false);

  const [showProjects, setShowProjects] = useState(true);
  const [showDrafts, setShowDrafts] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);

  const [companyImages, setCompanyImages] = useState(
    currentCompany.company_images_records || []
  );
  const [newCompanyImages, setNewCompanyImages] = useState([]);
  const [justUploadedCompanyImages, setJustUploadedCompanyImages] = useState([]);

  const MAX_COMPANY_IMAGES = 5;

  const handleCompanyImagesChange = (files) => {
    const totalCount = files.length + companyImages.length;
    if (totalCount > MAX_COMPANY_IMAGES) {
      enqueueSnackbar(
        intl.formatMessage({ id: "settings.company_images_max" }),
        { variant: "error" }
      );
      return;
    }
    const newPictures = files.filter(
      (file) => !justUploadedCompanyImages.includes(file)
    );
    setNewCompanyImages(newPictures);
  };

  useEffect(() => {
    const uploadCompanyImages = async () => {
      let uploadedCount = 0;
      const totalPictures = newCompanyImages.length;

      for (const picture of newCompanyImages) {
        try {
          await uploadPicture(
            picture,
            currentCompany.id,
            "company",
            "company_images",
            authenticityToken
          );
          setJustUploadedCompanyImages((prev) => [...prev, picture]);
          replaceCurrentRecruiter(dispatch, authenticityToken);
          uploadedCount++;

          if (uploadedCount === totalPictures) {
            enqueueSnackbar(
              intl.formatMessage({ id: totalPictures > 1 ? "settings.company_images_uploaded" : "settings.company_image_uploaded" }),
              { variant: "success" }
            );
          }
        } catch (err) {
          Bugsnag.notify(err);
          enqueueSnackbar(
            intl.formatMessage({ id: "settings.save_failed" }),
            { variant: "error" }
          );
        }
      }
    };

    if (newCompanyImages.length > 0) {
      uploadCompanyImages();
    }
  }, [newCompanyImages]);

  useEffect(() => {
    setCompanyImages(currentCompany.company_images_records || []);
  }, [currentCompany.company_images_records]);

  const handleCompanyImageDelete = (picture) => {
    sh.put("/purge_company_image", {
      authenticity_token: authenticityToken,
      picture: { id: picture.id },
    })
      .then(() => {
        setCompanyImages((prev) =>
          prev.filter((p) => p.picture !== picture)
        );
        replaceCurrentRecruiter(dispatch, authenticityToken);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        enqueueSnackbar(
          intl.formatMessage({ id: "settings.save_failed" }),
          { variant: "error" }
        );
      });
  };

  const {
    getRootProps: getCompanyImagesRootProps,
    getInputProps: getCompanyImagesInputProps,
  } = useDropzone({
    accept: [".png", ".jpeg", ".jpg"],
    onDrop: (acceptedFiles) => {
      handleCompanyImagesChange(acceptedFiles);
    },
    maxFiles: MAX_COMPANY_IMAGES,
  });

  const initialGeneralInfoValues = {
    logo: currentCompany.logo_url,
    cover_picture: currentCompany.cover_picture_url,
    name: currentCompany.name,
    location:
      currentCompany.city ||
      (currentCompany.region
        ? intl.formatMessage({ id: `regions.${currentCompany.region}` })
        : null) ||
      (currentCompany.country
        ? intl.formatMessage({ id: `countries.${currentCompany.country}` })
        : null),
    company_industry: currentCompany.company_industry,
    sub_sector: currentCompany.sub_sector || "",
    team_size: currentCompany.team_size,
    creation_year: currentCompany.creation_year,
    average_age: currentCompany.average_age,
  };

  const [generalInfoValues, setGeneralInfoValues] = useState(
    initialGeneralInfoValues
  );

  const initialDetailedInfoValues = {
    about: currentCompany.about,
    references: currentCompany.references,
    awards: currentCompany.awards,
    recruitment_process: (currentCompany.recruitment_process || []).sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    ),
  };

  const [detailedInfoValues, setDetailedInfoValues] = useState(
    initialDetailedInfoValues
  );
  const [recruitmentSteps, setRecruitmentSteps] = useState(
    initialDetailedInfoValues.recruitment_process || []
  );
  const [milestones, setMilestones] = useState(
    (currentCompany.company_milestones || [])
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((milestone, index) => ({
        id: milestone.id,
        year: milestone.year || "",
        title: milestone.title || "",
        description: milestone.description || "",
        icon: milestone.icon || "",
        position: milestone.position || index + 1,
      }))
  );
  const [deletedMilestoneIds, setDeletedMilestoneIds] = useState([]);

  const sortedProjects = sortDateRange(currentCompany?.projects);
  const draftProjectsCount = sortedProjects.filter(
    (project) => project.is_draft
  ).length;

  const handleManualCrop = () => {
    setCropable("cover_picture");
    setIsCropModalOpened(true);
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    let newValues = { ...generalInfoValues, [name]: value };

    if (name === "company_industry") {
      newValues.sub_sector = "";
    }

    setGeneralInfoValues(newValues);
  };

  const onPlacesSelect = (place) => {
    if (place.address_components) {
      let compPostalCode = "";
      let compLocality = "";
      let compAdministrativeAreaLevel2 = "";
      let compAdministrativeAreaLevel1 = "";
      let countryShortName = "";
      let regionValue = "";

      place.address_components.forEach((c) => {
        if (c.types.includes("postal_code")) {
          compPostalCode = c.long_name;
        }
        if (c.types.includes("locality")) {
          compLocality = c.long_name;
        }
        if (c.types.includes("administrative_area_level_2")) {
          compAdministrativeAreaLevel2 = c.long_name
            ?.toLowerCase()
            ?.replace(/ /g, "_");
        }
        if (c.types.includes("administrative_area_level_1")) {
          compAdministrativeAreaLevel1 = c.long_name
            ?.toLowerCase()
            ?.replace(/ /g, "_");
        }
        if (c.types.includes("country")) {
          countryShortName = c.short_name.toLowerCase();
        }
      });

      if (countryShortName === "fr") {
        regionValue = compAdministrativeAreaLevel1 || compAdministrativeAreaLevel2;
        regionValue = normalizeRegion(regionValue);
      } else if (countryShortName === "be") {
        if (compAdministrativeAreaLevel2 === "luxembourg") {
          regionValue = "be_luxembourg";
        } else {
          regionValue = compAdministrativeAreaLevel2 || compAdministrativeAreaLevel1;
        }
      } else {
        regionValue = compAdministrativeAreaLevel2 || compAdministrativeAreaLevel1;
      }

      let newValues = {
        ...generalInfoValues,
        location: place.formatted_address,
        postal_code: compPostalCode,
        city: compLocality,
        region: regionValue,
        country: countryShortName,
      };

      setGeneralInfoValues(newValues);
    }
  };

  const handleTextArea = (name) => (value) => {
    detailedInfoValues[name] = value;
    setDetailedInfoValues(detailedInfoValues);
  };

  const handleRecruitmentStepChange = (index, field, value) => {
    setRecruitmentSteps((prev) =>
      prev.map((step, i) => (i === index ? { ...step, [field]: value } : step))
    );
  };

  const addRecruitmentStep = () => {
    setRecruitmentSteps((prev) => [
      ...prev,
      { title: "", description: "", position: prev.length + 1 },
    ]);
  };

  const removeRecruitmentStep = (index) => {
    setRecruitmentSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((step, stepIndex) => ({ ...step, position: stepIndex + 1 }))
    );
  };

  const handleMilestoneChange = (index, field, value) => {
    setMilestones((prev) =>
      prev.map((milestone, i) =>
        i === index ? { ...milestone, [field]: value } : milestone
      )
    );
  };

  const addMilestone = () => {
    setMilestones((prev) => [
      ...prev,
      {
        year: "",
        title: "",
        description: "",
        icon: "",
        position: prev.length + 1,
      },
    ]);
  };

  const removeMilestone = (index) => {
    setMilestones((prev) => {
      const milestoneToRemove = prev[index];
      if (milestoneToRemove?.id) {
        setDeletedMilestoneIds((ids) => [...ids, milestoneToRemove.id]);
      }

      return prev
        .filter((_, i) => i !== index)
        .map((milestone, milestoneIndex) => ({
          ...milestone,
          position: milestoneIndex + 1,
        }));
    });
  };

  const syncMilestones = async () => {
    const milestonesPayload = milestones
      .filter((milestone) => milestone.title?.trim() && milestone.description?.trim() && milestone.year)
      .map((milestone, index) => ({
        ...milestone,
        year: Number(milestone.year),
        title: milestone.title.trim(),
        description: milestone.description.trim(),
        icon: milestone.icon?.trim() || null,
        position: index + 1,
      }));

    await Promise.all(
      deletedMilestoneIds.map((id) =>
        sh.delete(`company_milestones/${id}`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          data: {
            authenticity_token: authenticityToken,
          },
        })
      )
    );

    for (const milestone of milestonesPayload) {
      const milestonePayload = {
        company_milestone: {
          year: milestone.year,
          title: milestone.title,
          description: milestone.description,
          icon: milestone.icon,
          position: milestone.position,
        },
        authenticity_token: authenticityToken,
      };

      if (milestone.id) {
        await sh.put(`company_milestones/${milestone.id}`, milestonePayload);
      } else {
        await sh.post("company_milestones", milestonePayload);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const normalizedRecruitmentSteps = recruitmentSteps
      .filter((step) => step.title?.trim() || step.description?.trim())
      .map((step, index) => ({
        title: step.title?.trim() || "",
        description: step.description?.trim() || "",
        position: index + 1,
      }));

    sh.put(`${i18nLocale}/company/${currentCompany.id}`, {
      authenticity_token: authenticityToken,
      company: {
        ...generalInfoValues,
        ...detailedInfoValues,
        recruitment_process: normalizedRecruitmentSteps,
      },
    })
      .then(async () => {
        await syncMilestones();
        enqueueSnackbar(intl.formatMessage({ id: "settings.save_success" }), {
          variant: "success",
        });

        const url = new URL(window.location.href);
        url.searchParams.set("selectedSection", "company");
        window.location.assign(url.toString());
      })
      .catch((err) => {
        Bugsnag.notify(err);
        enqueueSnackbar(intl.formatMessage({ id: "settings.save_failed" }), {
          variant: "error",
        });
      });
  };

  const projectValues = {
    title: "",
    description: "",
    location: "",
    client_name: "",
    status: "planned",
    company_id: currentCompany.id,
  };

  const handleNewProjectClicked = (e) => {
    sh.post("projects", {
      authenticity_token: authenticityToken,
      project_data: projectValues,
    }).then((res) => {
      const createdProject = res.data;
      setSelectedProject(createdProject);
      replaceCurrentRecruiter(dispatch, authenticityToken);
      setIsEditModalOpen(true);
      setIsNew(true);
      setFirstPartAndNew(true);
    });
    e.preventDefault();
  };

  const deleteProject = () => {
    sh.delete(`projects/${selectedProject.id}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token: authenticityToken,
        selectedProject,
      },
    }).then(() => {
      replaceCurrentRecruiter(dispatch, authenticityToken);
    });
  };

  const handleProjectClicked = (project) => {
    setSelectedProject(project);
    setIsEditModalOpen(true);
    setIsNew(false);
  };

  const displayProjects = () => {
    setShowProjects(true);
    setShowDrafts(false);
  };

  const displayDrafts = () => {
    setShowProjects(false);
    setShowDrafts(true);
  };

  const projectsToShow = showProjects
    ? sortedProjects.filter((project) => !project.is_draft)
    : sortedProjects.filter((project) => project.is_draft);

  useEffect(() => {
    let project = sortedProjects.find((p) => p.id == selectedProject?.id);
    if (project) {
      setSelectedProject(project);
    }
  }, [sortedProjects.find((p) => p.id == selectedProject?.id)]);

  useEffect(() => {
    if (draftProjectsCount === 0) {
      setShowProjects(true);
    }
  }, [draftProjectsCount]);

  useEffect(() => {
    if (logo && typeof logo != "string") {
      uploadPicture(
        logo,
        currentCompany.id,
        "company",
        "logo",
        authenticityToken
      )
        .then((res) => {
          let newValues = { ...generalInfoValues, logo: res.company.logo_url };

          setGeneralInfoValues(newValues);
          setLogoUrl(res.company.logo_url);

          if (isCropped) {
            setIsCropped(false);
            setIsCropModalOpened(false);
            enqueueSnackbar(
              intl.formatMessage({ id: "settings.save_success" }),
              {
                variant: "success",
              }
            );
          } else {
            setCropable("logo");
            setIsCropModalOpened(true);
          }
        })
        .catch((err) => {
          Bugsnag.notify(err);
          enqueueSnackbar(intl.formatMessage({ id: "settings.save_failed" }), {
            variant: "error",
          });
        });
    }
  }, [logo]);

  useEffect(() => {
    if (coverPicture && typeof coverPicture != "string") {
      uploadPicture(
        coverPicture,
        currentCompany.id,
        "company",
        "cover_picture",
        authenticityToken
      )
        .then((res) => {
          let newValues = {
            ...generalInfoValues,
            cover_picture: res.company.cover_picture_url,
          };

          setGeneralInfoValues(newValues);
          setCoverPictureUrl(res.company.cover_picture_url);

          if (isCropped) {
            setIsCropped(false);
            setIsCropModalOpened(false);
            enqueueSnackbar(
              intl.formatMessage({ id: "settings.save_success" }),
              {
                variant: "success",
              }
            );
          } else {
            setCropable("cover_picture");
            setIsCropModalOpened(true);
          }
        })
        .catch(() => {
          enqueueSnackbar(intl.formatMessage({ id: "settings.save_failed" }), {
            variant: "error",
          });
        });
    }
  }, [coverPicture]);

  // ---- Reusable inline style fragments ----
  const sectionTitleSx = {
    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
    fontSize: "1.0625rem",
    color: "hsl(220, 25%, 12%)",
    letterSpacing: "-0.01em",
    m: 0,
    mb: "16px",
  };
  const subLabelSx = {
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontSize: "0.9375rem",
    color: "hsl(220, 25%, 12%)",
    m: 0,
    mb: "12px",
  };
  const hintSx = {
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.8125rem",
    color: "hsl(220, 10%, 46%)",
    m: 0,
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        {/* Section header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            mb: "24px",
          }}
        >
          <Box
            component="h2"
            sx={{
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              fontSize: "1.25rem",
              color: "hsl(178, 93%, 10%)",
              letterSpacing: "-0.01em",
              m: 0,
            }}
          >
            <FormattedMessage id="settings.company_page" />
          </Box>
          <Button
            variant="rounded"
            color="secondaryOutlined"
            href={`/${i18nLocale}/recruiter/company`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              width: "180px",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="settings.show" />
          </Button>
        </Box>

        {/* Branding: logo, cover, gallery */}
        <Box sx={cardSx}>
          <Box
            sx={{
              display: "flex",
              columnGap: "32px",
              rowGap: "24px",
              flexDirection: isDesktop ? "row" : "column",
              mb: "24px",
            }}
          >
            <Box>
              <Box component="p" sx={subLabelSx}>
                <FormattedMessage id="settings.logo" />
              </Box>
              <PictureDropzone
                handleFileInput={(file) => {
                  setLogo(file);
                  setGeneralInfoErrors({ ...generalInfoErrors, logo: "" });
                }}
                filesLimit={1}
                maxFileSize={204800}
                pictureUrl={generalInfoValues.logo}
                width="200px"
                height="200px"
                type="recruiter"
              />
              <Box component="p" sx={{ ...hintSx, mt: "8px" }}>
                <FormattedMessage id="settings.recommended_size" /> 250x250
              </Box>
            </Box>
            <Box style={{ flexGrow: 1 }}>
              <Box component="p" sx={subLabelSx}>
                <FormattedMessage id="settings.cover_picture" />
              </Box>
              <PictureDropzone
                handleFileInput={(file) => {
                  setCoverPicture(file);
                  setGeneralInfoErrors({
                    ...generalInfoErrors,
                    cover_picture: "",
                  });
                }}
                acceptedFiles={["image/png", "image/jpeg", "image/jpg"]}
                filesLimit={1}
                maxFileSize={2048000}
                backgroundSize="cover"
                pictureUrl={generalInfoValues.cover_picture}
                width="100%"
                height="200px"
                type="recruiter"
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: "8px",
                }}
              >
                <Box component="p" sx={hintSx}>
                  <FormattedMessage id="settings.recommended_size" /> 1000x350
                </Box>
                <Box
                  component="span"
                  onClick={handleManualCrop}
                  sx={{
                    cursor: "pointer",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    fontSize: "0.8125rem",
                    color: "hsl(153, 68%, 25%)",
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                  }}
                >
                  <FormattedMessage id="image_cropper.title" />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box component="p" sx={subLabelSx}>
              <FormattedMessage id="settings.company_gallery" />
            </Box>
            <Box
              style={{
                display: "flex",
                columnGap: "12px",
                alignItems: "flex-start",
              }}
            >
              <Box style={{ width: "auto" }}>
                <Box
                  {...getCompanyImagesRootProps()}
                  display="flex"
                  alignItems="center"
                >
                  <input
                    {...getCompanyImagesInputProps()}
                    disabled={companyImages.length >= MAX_COMPANY_IMAGES}
                  />
                  {companyImages.length < MAX_COMPANY_IMAGES && (
                    <ImagePlus
                      size={44}
                      color="hsl(153, 68%, 25%)"
                      style={{ cursor: "pointer" }}
                    />
                  )}
                </Box>
                {companyImages.length >= MAX_COMPANY_IMAGES && (
                  <Box component="p" sx={hintSx}>
                    <FormattedMessage id="settings.company_images_max" />
                  </Box>
                )}
              </Box>
              <Box
                style={{
                  width: "100%",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  justifyContent:
                    companyImages.length === 0 ? "center" : "flex-start",
                }}
              >
                {companyImages.length === 0 ? (
                  <Box
                    component="p"
                    sx={{
                      ...hintSx,
                      fontSize: "0.875rem",
                      alignSelf: "center",
                    }}
                  >
                    <FormattedMessage id="settings.company_images_empty" />
                  </Box>
                ) : (
                  companyImages.map((record, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        width: "120px",
                        height: "90px",
                        "& img": {
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "8px",
                        },
                        "& .delete-button": {
                          position: "absolute",
                          top: "2px",
                          right: "2px",
                          background: "rgba(255, 255, 255, 0.8)",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          color: "rgba(0, 0, 0, 0.6)",
                        },
                      }}
                    >
                      <img src={record.url} alt={`Company image ${index + 1}`} />
                      <Box
                        className="delete-button"
                        onClick={() => handleCompanyImageDelete(record.picture)}
                      >
                        <X size={14} />
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
            <Box component="p" sx={{ ...hintSx, mt: "8px" }}>
              <FormattedMessage id="settings.company_images_hint" />
            </Box>
          </Box>
        </Box>

        {/* General info */}
        <Box sx={cardSx}>
          <Box component="h3" sx={sectionTitleSx}>
            <FormattedMessage id="settings.general_info" />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                sx={{ ...inputSx, mb: "16px" }}
                value={generalInfoValues.name}
                type="text"
                name="name"
                onBlur={handleInputValue}
                onChange={handleInputValue}
                variant="outlined"
                label={<FormattedMessage id="settings.company_name" />}
                {...(generalInfoErrors["name"] && {
                  error: true,
                  helperText: generalInfoErrors["name"],
                })}
              />
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ ...inputSx, mb: "16px" }}
              >
                <InputLabel id="field-label">
                  <FormattedMessage id="company.company_industry.company_industry" />
                </InputLabel>
                <Select
                  name="company_industry"
                  value={generalInfoValues.company_industry}
                  onChange={handleInputValue}
                  label={
                    <FormattedMessage id="company.company_industry.company_industry" />
                  }
                >
                  {companyIndustryMap.map((value) => {
                    return (
                      <MenuItem value={value} key={value}>
                        <FormattedMessage
                          id={"company.company_industry." + value}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {companySubSectorMap[generalInfoValues.company_industry]?.length >
                0 && (
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ ...inputSx, mb: "16px" }}
                >
                  <InputLabel id="sub-sector-label">
                    <FormattedMessage id="devise.signup.sub_sector" />
                  </InputLabel>
                  <Select
                    name="sub_sector"
                    value={generalInfoValues.sub_sector}
                    onChange={handleInputValue}
                    label={<FormattedMessage id="devise.signup.sub_sector" />}
                  >
                    {companySubSectorMap[
                      generalInfoValues.company_industry
                    ].map((sub) => (
                      <MenuItem value={sub} key={sub}>
                        <FormattedMessage
                          id={"company.company_sub_sector." + sub}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                sx={inputSx}
                color="secondary"
                name="location"
                variant="outlined"
                placeholder={intl.formatMessage({
                  id: "devise.signup.search_city_or_zipcode",
                })}
                InputProps={{
                  inputComponent: ({ ...props }) => (
                    <Autocomplete
                      /* eslint-disable no-undef */
                      apiKey={process.env.GOOGLE_AUTOCOMPLETE_API_KEY}
                      multiple
                      language="en"
                      {...props}
                      onPlaceSelected={onPlacesSelect}
                      options={{
                        types: ["(regions)"],
                        componentRestrictions: {
                          country: ["be", "lu", "fr"],
                        },
                      }}
                      defaultValue={generalInfoValues.location}
                    />
                  ),
                }}
                {...(generalInfoErrors["locations"] && {
                  error: true,
                  helperText: generalInfoErrors["locations"],
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                sx={{ ...inputSx, mb: "16px" }}
                value={generalInfoValues.creation_year}
                type="number"
                name="creation_year"
                onBlur={handleInputValue}
                onChange={handleInputValue}
                variant="outlined"
                label={<FormattedMessage id="settings.creation_year" />}
                InputProps={{
                  inputProps: {
                    max: new Date().getFullYear(),
                    min: 1800,
                  },
                }}
                {...(generalInfoErrors["creation_year"] && {
                  error: true,
                  helperText: generalInfoErrors["creation_year"],
                })}
              />
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ ...inputSx, mb: "16px" }}
              >
                <InputLabel id="field-label">
                  <FormattedMessage id="settings.team_size" />
                </InputLabel>
                <Select
                  name="team_size"
                  value={generalInfoValues.team_size}
                  onChange={handleInputValue}
                  label={<FormattedMessage id="settings.team_size" />}
                >
                  {companySizeMap.map((value) => (
                    <MenuItem value={value} key={value}>
                      <FormattedMessage
                        id={"company.company_size.options." + value}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                sx={inputSx}
                value={generalInfoValues.average_age}
                type="number"
                name="average_age"
                onBlur={handleInputValue}
                onChange={handleInputValue}
                variant="outlined"
                label={<FormattedMessage id="settings.average_age" />}
                InputProps={{
                  inputProps: {
                    max: 99,
                    min: 18,
                  },
                }}
                {...(generalInfoErrors["average_age"] && {
                  error: true,
                  helperText: generalInfoErrors["average_age"],
                })}
              />
            </Grid>
          </Grid>
          <Box textAlign="center" style={{ marginTop: "24px" }}>
            <Button
              variant="rounded"
              color="secondaryContained"
              type="submit"
              style={{
                width: "180px",
                textTransform: "none",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="save" />
            </Button>
          </Box>
        </Box>

        {/* Company details */}
        <Box sx={cardSx}>
          <Box style={{ marginBottom: "32px" }}>
            <Box component="p" sx={subLabelSx}>
              <FormattedMessage id="company.about" />
            </Box>
            <RichTextEditor
              height="120px"
              onChange={handleTextArea("about")}
              defaultValue={detailedInfoValues.about}
              {...(detailedInfoErrors["about"] && {
                error: true,
                helperText: detailedInfoErrors["about"],
              })}
            />
          </Box>
          <Box style={{ marginBottom: "32px" }}>
            <Box component="p" sx={subLabelSx}>
              <FormattedMessage id="company.references" />
            </Box>
            <RichTextEditor
              height="120px"
              onChange={handleTextArea("references")}
              defaultValue={detailedInfoValues.references}
              {...(detailedInfoErrors["references"] && {
                error: true,
                helperText: detailedInfoErrors["references"],
              })}
            />
          </Box>
          <Box style={{ marginBottom: "32px" }}>
            <Box component="p" sx={subLabelSx}>
              <FormattedMessage id="company.awards" />
            </Box>
            <RichTextEditor
              height="120px"
              onChange={handleTextArea("awards")}
              defaultValue={detailedInfoValues.awards}
              {...(detailedInfoErrors["awards"] && {
                error: true,
                helperText: detailedInfoErrors["awards"],
              })}
            />
          </Box>

          <Box style={{ marginBottom: "32px" }}>
            <Box component="p" sx={{ ...subLabelSx, mb: "8px" }}>
              <FormattedMessage id="settings.recruitment_steps" />
            </Box>
            <Box component="p" sx={{ ...hintSx, mb: "16px" }}>
              <FormattedMessage id="settings.recruitment_steps_hint" />
            </Box>
            {recruitmentSteps.map((step, index) => (
              <Box
                key={`recruitment-step-${index}`}
                style={{
                  border: "1px solid hsl(220, 13%, 91%)",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "12px",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      fontSize: "0.9375rem",
                      color: "hsl(220, 25%, 12%)",
                    }}
                  >
                    <FormattedMessage
                      id="settings.recruitment_step_n"
                      values={{ n: index + 1 }}
                    />
                  </Box>
                  <Button
                    onClick={() => removeRecruitmentStep(index)}
                    startIcon={<Trash2 size={16} />}
                    style={{
                      color: "hsl(0, 72%, 51%)",
                      textTransform: "none",
                    }}
                  >
                    <FormattedMessage id="settings.remove" />
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  sx={{ ...inputSx, mb: "12px" }}
                  label={intl.formatMessage({
                    id: "settings.recruitment_step_title",
                  })}
                  value={step.title || ""}
                  onChange={(e) =>
                    handleRecruitmentStepChange(index, "title", e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  sx={inputSx}
                  label={intl.formatMessage({
                    id: "settings.recruitment_step_description",
                  })}
                  value={step.description || ""}
                  onChange={(e) =>
                    handleRecruitmentStepChange(
                      index,
                      "description",
                      e.target.value
                    )
                  }
                  multiline
                  minRows={3}
                />
              </Box>
            ))}
            <Button
              onClick={addRecruitmentStep}
              startIcon={<PlusCircle size={18} />}
              style={{
                color: "hsl(153, 68%, 25%)",
                textTransform: "none",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="settings.add_recruitment_step" />
            </Button>
          </Box>

          <Box style={{ marginBottom: "32px" }}>
            <Box component="p" sx={subLabelSx}>
              <FormattedMessage id="settings.company_milestones" />
            </Box>
            {milestones.map((milestone, index) => (
              <Box
                key={milestone.id || `milestone-${index}`}
                style={{
                  border: "1px solid hsl(220, 13%, 91%)",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "12px",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      fontSize: "0.9375rem",
                      color: "hsl(220, 25%, 12%)",
                    }}
                  >
                    <FormattedMessage
                      id="settings.milestone_n"
                      values={{ n: index + 1 }}
                    />
                  </Box>
                  <Button
                    onClick={() => removeMilestone(index)}
                    startIcon={<Trash2 size={16} />}
                    style={{
                      color: "hsl(0, 72%, 51%)",
                      textTransform: "none",
                    }}
                  >
                    <FormattedMessage id="settings.remove" />
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      sx={inputSx}
                      type="number"
                      label={intl.formatMessage({
                        id: "settings.milestone_year",
                      })}
                      value={milestone.year || ""}
                      onChange={(e) =>
                        handleMilestoneChange(index, "year", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <TextField
                      fullWidth
                      sx={inputSx}
                      label={intl.formatMessage({
                        id: "settings.milestone_title",
                      })}
                      value={milestone.title || ""}
                      onChange={(e) =>
                        handleMilestoneChange(index, "title", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      sx={inputSx}
                      label={intl.formatMessage({
                        id: "settings.milestone_description",
                      })}
                      value={milestone.description || ""}
                      onChange={(e) =>
                        handleMilestoneChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              onClick={addMilestone}
              startIcon={<PlusCircle size={18} />}
              style={{
                color: "hsl(153, 68%, 25%)",
                textTransform: "none",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="settings.add_milestone" />
            </Button>
          </Box>

          <Box textAlign="center">
            <Button
              variant="rounded"
              color="secondaryContained"
              type="submit"
              style={{
                width: "180px",
                textTransform: "none",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="save" />
            </Button>
          </Box>
        </Box>

        {/* Projects */}
        <Box sx={cardSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "24px",
              mb: "16px",
            }}
          >
            <Box
              component="span"
              onClick={displayProjects}
              sx={{
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                fontSize: "1.0625rem",
                color: "hsl(220, 25%, 12%)",
                cursor: draftProjectsCount > 0 ? "pointer" : "default",
                textDecoration:
                  showProjects && draftProjectsCount > 0 ? "underline" : "none",
                textUnderlineOffset: "4px",
              }}
            >
              <FormattedMessage id="company.projects" />
            </Box>
            {draftProjectsCount > 0 && (
              <Box
                component="span"
                onClick={displayDrafts}
                sx={{
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.9375rem",
                  color: "hsl(220, 10%, 46%)",
                  cursor: "pointer",
                  textDecoration: showDrafts ? "underline" : "none",
                  textUnderlineOffset: "4px",
                }}
              >
                <FormattedMessage
                  id="project.drafts"
                  values={{ count: draftProjectsCount }}
                />
              </Box>
            )}
          </Box>

          {projectsToShow.length > 0 ? (
            <Box>
              {projectsToShow
                .slice(0, showAllProjects ? projectsToShow.length : 3)
                .map((project, i) => (
                  <Box key={`project-${i}`}>
                    <ProjectSettingsView
                      project={project}
                      editable={true}
                      onEdit={handleProjectClicked}
                    />
                    {(showAllProjects || i !== 2) &&
                      i !== projectsToShow.length - 1 && (
                      <Box
                        sx={{
                          borderTop: "1px solid hsl(220, 13%, 91%)",
                          my: "16px",
                        }}
                      />
                    )}
                  </Box>
                ))}
              {projectsToShow.length > 3 && (
                <Button
                  variant="rounded"
                  color="secondaryOutlined"
                  style={{ marginTop: "16px", textTransform: "none" }}
                  onClick={() => setShowAllProjects(!showAllProjects)}
                >
                  <FormattedMessage
                    id={showAllProjects ? "see_less" : "see_more"}
                  />
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: "16px",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                fontSize: "0.9375rem",
                color: "hsl(220, 10%, 46%)",
              }}
            >
              <FormattedMessage id="project.no_project_added" />
            </Box>
          )}

          <Box textAlign="center" style={{ marginTop: "32px" }}>
            <Button
              variant="rounded"
              color="secondaryContained"
              onClick={handleNewProjectClicked}
              style={{
                textTransform: "none",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <PlusCircle size={18} style={{ marginRight: "8px" }} />
              <FormattedMessage id="settings.add_project" />
            </Button>
          </Box>

          <Modal
            open={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              if (firstPartAndNew) {
                deleteProject();
                setFirstPartAndNew(false);
              }
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              overflow: "hidden !important",
            }}
          >
            <EditProjectModal
              closeModal={() => setIsEditModalOpen(false)}
              project={selectedProject}
              isNewProject={isNew}
              isNew={isNew}
              setFirstPartAndNew={setFirstPartAndNew}
            />
          </Modal>
        </Box>
      </Box>

      <Dialog
        open={isCropModalOpened}
        onClose={() => setIsCropModalOpened(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        fullWidth="xl"
        maxWidth
      >
        <ImageCropper
          ratio={
            cropable == "logo" ? pictureRatios.logo : pictureRatios.coverPicture
          }
          src={cropable == "logo" ? logoUrl : coverPictureUrl}
          filename={
            cropable == "logo"
              ? logoUrl.split("filename=")[1] || `logo_${currentCompany.name}`
              : coverPictureUrl.split("filename=")[1] ||
                `cover_${currentCompany.name}`
          }
          closeModal={() => setIsCropModalOpened(false)}
          handleNewImg={(img, callback) => {
            setIsCropped(true);
            if (cropable == "logo") {
              setLogo(img[0]);
              setGeneralInfoErrors({
                ...generalInfoErrors,
                logo: "",
              });
            } else {
              setCoverPicture(img[0]);
              setGeneralInfoErrors({
                ...generalInfoErrors,
                cover_picture: "",
              });
            }
            if (callback) {
              setTimeout(() => {
                callback();
              }, 1500);
            }
          }}
        />
      </Dialog>
    </>
  );
};

export default Company;
